import { AfterViewInit, Component, inject, OnInit, Signal, ViewEncapsulation } from '@angular/core';
import { WagoApiNotifierService } from '@shared/service/wago-api-notifier.service';
import { InitManager } from '@shared/manager/init.manager';
import { ResizeUtils } from '@shared/util/resize.utils';
import { NotificationVO } from '@shared/vo/notification.vo';
import { DeviceUtils } from '@shared/util/device-utils';
import { ProjectHelperInitializer } from '@project/helper/project-helper.initializer';
import { ArticleConnectProcessorRegistryInitializer } from '@project/components/article/processor/prepare/connect/article-connect-processor-registry.initializer';
import { ArticleInsertProcessorRegistryInitializer } from '@project/components/article/processor/prepare/insert/article-insert-processor-registry.initializer';
import { ArticlePrepareProcessorRegistryInitializer } from '@project/components/article/processor/prepare/article-prepare-processor-registry.initializer';
import { InsertArticleServiceInitializer } from '@project/components/article/service/insert-article-service.initializer';
import { ClearServiceInitializer } from '@project/service/clear-service.initializer';
import { ArticleContainerFactoryInitializer } from '@3d-scene/model3d/article-container-factory.initializer';
import { OpenLabelEditorServiceInitializer } from '@project/components/label/label-editor/service/open-label-editor-service.initializer';
import { DetachArticleUsageServiceInitializer } from '@project/service/detach-article-usage-service.initializer';
import { ProServeProjectImportProcessorInitializer } from '@project/components/import-export/processor/pro-serve-project-import-processor.initializer';
import { AddCodingArticleProcessorInitializer } from '@project/components/article/processor/add-coding-article-processor.initializer';
import { SystemNotificationManager } from '@project/components/article/manager/system-notification.manager';
import { TabTitleService } from '@project/service/tab-title.service';
import { ElbridgeService } from '@project/components/project/elbridge/service/elbridge.service';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'body',
  templateUrl: 'app.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, AfterViewInit {
  private routerNavigationSubscrption: Subscription;

  public loading = true;

  public mainNotifications: NotificationVO[] = [];
  public hasTemporaryMainNotifications: boolean = false;

  constructor(
    private initManager: InitManager,
    private systemNotificationManager: SystemNotificationManager,
    private projectHelperInitializer: ProjectHelperInitializer,
    private articleConnectProcessorRegistryInitializer: ArticleConnectProcessorRegistryInitializer,
    private articleInsertProcessorRegistryInitializer: ArticleInsertProcessorRegistryInitializer,
    private articlePrepareProcessorRegistryInitializer: ArticlePrepareProcessorRegistryInitializer,
    private insertArticleServiceInitializer: InsertArticleServiceInitializer,
    private clearServiceInitializer: ClearServiceInitializer,
    private articleContainerFactoryInitializer: ArticleContainerFactoryInitializer,
    private openLabelEditorServiceInitializer: OpenLabelEditorServiceInitializer,
    private detachArticleUsageServiceInitializer: DetachArticleUsageServiceInitializer,
    private proServeProjectImportProcessorInitializer: ProServeProjectImportProcessorInitializer,
    private addCodingArticleProcessorInitializer: AddCodingArticleProcessorInitializer,
    private tabTitleService: TabTitleService,
    private elbridgeService: ElbridgeService,
    private router: Router,
  ) {
    if (!DeviceUtils.isIos()) {
      window.onbeforeunload = (e) => {
        const confirmationMessage = 'Do you really want to close configurator?';
        e.returnValue = confirmationMessage;
        return confirmationMessage;
      };
    }
    inject(WagoApiNotifierService).init();
    this.routerNavigationSubscrption = this.router.events.pipe(takeUntilDestroyed()).subscribe(
      (event): void => {
        if (event instanceof NavigationStart) {
          this.loading = true;
        } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
          this.loading = false;
          this.unsubscribeRouterNavigation();
        }
      });
  }

  private unsubscribeRouterNavigation(): void {
    if (this.routerNavigationSubscrption) {
      this.routerNavigationSubscrption.unsubscribe();
    }
  }

  public get isCdcAvailable(): Signal<boolean> {
    return this.initManager.isCdcAvailable;
  }

  public getNotificationText(notification: NotificationVO): string {
    return this.systemNotificationManager.getNotificationText(notification);
  }

  public closeNotifications(): void {
    this.mainNotifications = [];
  }

  public confirmNotifications(): void {
    this.systemNotificationManager.applyConfirmationToAllNotificationsAtLocation("main");
    this.closeNotifications();
  }

  public async updateNotifications(): Promise<void> {
    const notifications = await this.systemNotificationManager.getNotificationsForLocation("main");
    this.mainNotifications = notifications;
    this.hasTemporaryMainNotifications = this.systemNotificationManager.includesTemporaryNotification(notifications);
  }

  private async initUserCentrics() {
    const isConsentRequired = await (<Promise<boolean>>(<any>window).__ucCmp.isConsentRequired());
    if (isConsentRequired) {
      (<any>window).__ucCmp.showFirstLayer();
    }
  }

  async ngOnInit(): Promise<void> {
    this.initManager.init();
    this.projectHelperInitializer.initialize();
    this.articleConnectProcessorRegistryInitializer.initialize();
    this.articleInsertProcessorRegistryInitializer.initialize();
    this.articlePrepareProcessorRegistryInitializer.initialize();
    this.insertArticleServiceInitializer.initialize();
    this.clearServiceInitializer.initialize();
    this.articleContainerFactoryInitializer.initialize();
    this.openLabelEditorServiceInitializer.initialize();
    this.detachArticleUsageServiceInitializer.initialize();
    this.proServeProjectImportProcessorInitializer.initialize();
    this.addCodingArticleProcessorInitializer.initialize();
    this.tabTitleService.initialize()
    await this.elbridgeService.initElbridge();
    await this.updateNotifications();
    this.initUserCentrics();
  }


  ngAfterViewInit(): void {
    setTimeout(() => {
      if (ResizeUtils.needForResize) {
        ResizeUtils.resizeToFitWidth();
      }
    }, 0);
  }
}
